/**
 * Admin · Review Follow-up Funnel Tile (Phase 2L-A82)
 *
 * Read-only tile for the admin dashboard. Shows the pipeline:
 *   reports published → follow-ups sent → invites sent →
 *   vouchers applied → vouchers redeemed → bookings paid
 *
 * NEVER rendered for lab technicians (revenue/marketing surface).
 * NEVER mutates state — pure GET on a single read-only endpoint.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp, ArrowRight, Loader2, AlertTriangle, RefreshCw,
  FileCheck2, MailCheck, Send, Ticket, CheckCircle2, Receipt,
} from "lucide-react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";

const WINDOWS = [
  { id: "7d",  label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "90d", label: "90 days" },
  { id: "all", label: "All time" },
];

// Icon per stage — keeps the funnel scannable at a glance.
const STAGE_ICON = {
  reports_published:     FileCheck2,
  followups_sent:        MailCheck,
  invites_sent:          Send,
  vouchers_applied:      Ticket,
  vouchers_redeemed:     CheckCircle2,
  voucher_bookings_paid: Receipt,
};

function fmtPct(rate) {
  if (rate == null || Number.isNaN(rate)) return "—";
  return `${(Number(rate) * 100).toFixed(0)}%`;
}

export function ReviewFunnelTile() {
  const [window, setWindow] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/admin/review-followup/funnel", {
        params: { window, include_smoke: false },
      });
      setData(res.data);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [window]);

  useEffect(() => { load(); }, [load]);

  const stages = data?.stages || [];
  const conversions = data?.conversions || [];
  // Build a lookup so we can render the conversion under each
  // stage's "from" → "to" arrow without re-iterating.
  const convByFrom = Object.fromEntries(conversions.map((c) => [c.from, c]));

  return (
    <div
      data-testid="admin-review-funnel-tile"
      className="rounded-3xl bg-white border border-slate-200 p-6"
    >
      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-cyan-600" />
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">
            Review follow-up funnel
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="admin-review-funnel-window"
            value={window}
            onChange={(e) => setWindow(e.target.value)}
            disabled={loading}
            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs"
          >
            {WINDOWS.map((w) => (
              <option key={w.id} value={w.id}>{w.label}</option>
            ))}
          </select>
          <button
            type="button"
            data-testid="admin-review-funnel-refresh"
            title="Refresh"
            onClick={load}
            disabled={loading}
            className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-700"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {error ? (
        <div
          data-testid="admin-review-funnel-error"
          className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>{error}</div>
        </div>
      ) : loading && !data ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading funnel…
        </div>
      ) : (
        <>
          {/* Stage strip — horizontal scroll on small screens. */}
          <div className="flex items-stretch gap-2 overflow-x-auto pb-1">
            {stages.map((s, i) => {
              const Icon = STAGE_ICON[s.id] || FileCheck2;
              const conv = convByFrom[s.id];
              const isLast = i === stages.length - 1;
              return (
                <div key={s.id} className="flex items-stretch shrink-0">
                  <div
                    data-testid={`admin-review-funnel-stage-${s.id}`}
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 min-w-[120px]"
                  >
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                      <Icon className="w-3 h-3" />
                      <span className="truncate">{s.label}</span>
                    </div>
                    <div
                      data-testid={`admin-review-funnel-count-${s.id}`}
                      className="text-2xl font-display font-bold text-slate-900 leading-tight mt-1"
                    >
                      {s.count.toLocaleString()}
                    </div>
                  </div>
                  {!isLast && (
                    <div className="flex flex-col items-center justify-center px-1 text-slate-400">
                      <ArrowRight className="w-4 h-4" />
                      <div
                        data-testid={`admin-review-funnel-rate-${s.id}`}
                        className="text-[10px] font-semibold mt-0.5"
                        title={conv?.note || ""}
                      >
                        {fmtPct(conv?.rate)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            <div>
              Smoke-test rows excluded. Counts use each stage&apos;s own event timestamp.
            </div>
            <Link
              to="/admin/referral-invites"
              data-testid="admin-review-funnel-open-invites"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 hover:bg-cyan-50 text-xs font-semibold text-slate-700 border border-slate-200"
            >
              View invite records <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
